import React, { useEffect, useMemo, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Edit,
  Eye,
  IdCard,
  Image as ImageIcon,
  Search,
  ShieldCheck,
  Trash2,
  X,
} from "lucide-react";
import "./Orders.css";
import { adminApi } from "../../utils/adminApi";

const STATUS_FLOW = ["Pending", "Assigned", "Pickup Started", "In Service", "Completed"];
const STATUS_OPTIONS = [...STATUS_FLOW, "Cancelled"];
const PICKUP_OPTIONS = ["Pickup & Drop", "Self Drop"];
const PAGE_SIZE_OPTIONS = [10, 20, 50];

const formatBookingId = (order = {}) => {
  if (order.trackingId) return order.trackingId;
  return `#CS-${String(order._id || "").slice(-6).toUpperCase()}`;
};

const toNumberOrEmpty = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : "";
};

const getOrderMapUrl = (order = {}) => {
  if (order.mapUrl) return order.mapUrl;
  const lat = Number(order?.liveLocation?.lat);
  const lng = Number(order?.liveLocation?.lng);
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return `https://www.google.com/maps?q=${lat},${lng}`;
  }
  return "";
};

const getPickupMapUrl = (order = {}) => {
  if (order.pickupMapUrl) return order.pickupMapUrl;
  const pickupAddress = String(order.pickupAddress || "").trim();
  if (!pickupAddress) return "";
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(pickupAddress)}`;
};

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(PAGE_SIZE_OPTIONS[0]);
  const [visitOtpInput, setVisitOtpInput] = useState("");
  const [modalActionLoading, setModalActionLoading] = useState(false);

  const fetchOrdersAndTechnicians = async () => {
    try {
      setLoading(true);
      const [ordersRes, techniciansRes] = await Promise.all([
        adminApi.get("/api/bookings"),
        adminApi.get("/api/technicians"),
      ]);

      setOrders(Array.isArray(ordersRes.data) ? ordersRes.data : []);
      setTechnicians(Array.isArray(techniciansRes.data) ? techniciansRes.data : []);
    } catch (error) {
      alert(error.response?.data?.error || "Unable to fetch orders. Please login again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrdersAndTechnicians();
  }, []);

  const technicianById = useMemo(() => {
    const map = new Map();
    technicians.forEach((tech) => {
      map.set(String(tech._id), tech);
    });
    return map;
  }, [technicians]);

  const sortedOrders = useMemo(() => {
    return [...orders].sort((a, b) => {
      const leftTime = new Date(a?.createdAt || 0).getTime();
      const rightTime = new Date(b?.createdAt || 0).getTime();
      return rightTime - leftTime;
    });
  }, [orders]);

  const filteredOrders = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return sortedOrders;

    return sortedOrders.filter((order) => {
      const searchableText = [
        formatBookingId(order),
        order?.name,
        order?.phone,
        order?.email,
        order?.brand,
        order?.service,
        order?.pickupOption,
        order?.technicianName,
        order?.technician,
        order?.status,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(query);
    });
  }, [sortedOrders, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / rowsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const pageStartIndex = (safePage - 1) * rowsPerPage;

  const paginatedOrders = useMemo(() => {
    return filteredOrders.slice(pageStartIndex, pageStartIndex + rowsPerPage);
  }, [filteredOrders, pageStartIndex, rowsPerPage]);

  const visibleStart = filteredOrders.length === 0 ? 0 : pageStartIndex + 1;
  const visibleEnd = Math.min(pageStartIndex + rowsPerPage, filteredOrders.length);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, rowsPerPage]);

  useEffect(() => {
    setCurrentPage((previous) => Math.min(previous, totalPages));
  }, [totalPages]);

  const getNextStatus = (currentStatus = "Pending") => {
    const currentIndex = STATUS_FLOW.indexOf(currentStatus);
    if (currentIndex === -1 || currentIndex === STATUS_FLOW.length - 1) {
      return STATUS_FLOW[0];
    }
    return STATUS_FLOW[currentIndex + 1];
  };

  const toggleStatus = async (order) => {
    const nextStatus = getNextStatus(order.status || "Pending");
    try {
      const res = await adminApi.put(`/api/bookings/${order._id}/status`, {
        status: nextStatus,
      });
      setOrders((prev) => prev.map((item) => (item._id === order._id ? res.data : item)));
      if (selectedOrder?._id === order._id) setSelectedOrder(res.data);
      if (editingOrder?._id === order._id) {
        setEditingOrder(res.data);
      }
    } catch (error) {
      alert(error.response?.data?.error || "Status update failed");
    }
  };

  const deleteOrder = async (id) => {
    if (!window.confirm("Delete this order?")) return;

    try {
      await adminApi.delete(`/api/bookings/${id}`);
      setOrders((prev) => prev.filter((item) => item._id !== id));
      if (selectedOrder?._id === id) setSelectedOrder(null);
      if (editingOrder?._id === id) setEditingOrder(null);
    } catch (error) {
      alert(error.response?.data?.error || "Delete failed");
    }
  };
  const [toast, setToast] = useState({
  message: "",
  type: "", // success | error
});

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => {
      setToast({ message: "", type: "" });
    }, 3000);
  };

  const patchOrderInState = (updatedOrder) => {
    if (!updatedOrder?._id) return;
    setOrders((prev) => prev.map((item) => (item._id === updatedOrder._id ? updatedOrder : item)));
    if (selectedOrder?._id === updatedOrder._id) {
      setSelectedOrder(updatedOrder);
    }
  };

  const openEdit = (order) => {
    const lat = toNumberOrEmpty(order?.liveLocation?.lat);
    const lng = toNumberOrEmpty(order?.liveLocation?.lng);
    const technicianId = order?.technicianId ? String(order.technicianId) : "";
    const selectedTech = technicianId ? technicianById.get(technicianId) : null;

    setEditingOrder({
      ...order,
      technicianId,
      technician: order.technician || order.technicianName || "",
      technicianName: order.technicianName || order.technician || selectedTech?.name || "",
      technicianPhone: order.technicianPhone || selectedTech?.phone || "",
      technicianEmployeeId: order.technicianEmployeeId || selectedTech?.employeeId || "",
      technicianPhoto: order.technicianPhoto || selectedTech?.photoUrl || "",
      technicianVerified: order.technicianVerified ?? selectedTech?.isVerified ?? false,
      pickupAddress: order.pickupAddress || "",
      pickupPhone: order.pickupPhone || order.phone || "",
      adminNote: order.adminNote || "",
      liveLat: lat,
      liveLng: lng,
    });
  };

  const saveUpdate = async () => {
    if (!editingOrder?._id) return;

    const selectedTech = editingOrder.technicianId
      ? technicianById.get(String(editingOrder.technicianId))
      : null;
    const technicianName =
      selectedTech?.name || editingOrder.technicianName || editingOrder.technician || "";
    const technicianPhone = selectedTech?.phone || editingOrder.technicianPhone || "";
    const technicianEmployeeId =
      selectedTech?.employeeId || editingOrder.technicianEmployeeId || "";
    const technicianPhoto = selectedTech?.photoUrl || editingOrder.technicianPhoto || "";
    const technicianVerified = selectedTech?.isVerified ?? editingOrder.technicianVerified ?? false;

    const payload = {
      name: editingOrder.name,
      phone: editingOrder.phone,
      email: editingOrder.email,
      status: editingOrder.status || "Pending",
      service: editingOrder.service,
      pickupOption: editingOrder.pickupOption || "Pickup & Drop",
      address: editingOrder.address,
      pickupAddress: editingOrder.pickupAddress || "",
      pickupPhone: editingOrder.pickupPhone || "",
      technicianId: editingOrder.technicianId || null,
      technician: technicianName,
      technicianName,
      technicianPhone,
      technicianEmployeeId,
      technicianPhoto,
      technicianVerified: Boolean(technicianVerified),
      adminNote: editingOrder.adminNote,
    };

    const lat = toNumberOrEmpty(editingOrder.liveLat);
    const lng = toNumberOrEmpty(editingOrder.liveLng);
    if (lat !== "" && lng !== "") {
      payload.lat = Number(lat);
      payload.lng = Number(lng);
    }

   try {
  const res = await adminApi.put(
    `/api/bookings/${editingOrder._id}`,
    payload
  );

  setOrders((prev) =>
    prev.map((item) =>
      item._id === editingOrder._id ? res.data : item
    )
  );

  setEditingOrder(null);

  // Show Success Toast
  showToast("Booking updated successfully!", "success");

} catch (error) {
  showToast(error.response?.data?.error || "Update failed", "error");
};

  };

  const sendTechnicianUpdateEmail = async () => {
    if (!selectedOrder?._id) return;
    try {
      setModalActionLoading(true);
      const res = await adminApi.post(`/api/bookings/${selectedOrder._id}/send-assignment-email`);
      patchOrderInState(res.data);
      if (res.data?.assignmentEmailSent) {
        showToast("Technician update email sent to customer.", "success");
      } else {
        showToast("Email not sent. Check SMTP config.", "error");
      }
    } catch (error) {
      showToast(error.response?.data?.error || "Unable to send update email", "error");
    } finally {
      setModalActionLoading(false);
    }
  };

  const sendVisitOtpToCustomer = async () => {
    if (!selectedOrder?._id) return;
    try {
      setModalActionLoading(true);
      const res = await adminApi.post(`/api/bookings/${selectedOrder._id}/resend-otp`);
      patchOrderInState(res.data);
      if (res.data?.otpEmailSent) {
        showToast("OTP sent to customer Gmail.", "success");
      } else {
        showToast("OTP generated but email not sent. Check SMTP.", "error");
      }
    } catch (error) {
      showToast(error.response?.data?.error || "Unable to send OTP", "error");
    } finally {
      setModalActionLoading(false);
    }
  };

  const verifyVisitOtp = async () => {
    if (!selectedOrder?._id) return;
    const otp = String(visitOtpInput || "").trim();
    if (!/^\d{6}$/.test(otp)) {
      showToast("Enter valid 6-digit OTP", "error");
      return;
    }
    try {
      setModalActionLoading(true);
      const res = await adminApi.post(`/api/bookings/${selectedOrder._id}/verify-otp`, { otp });
      patchOrderInState(res.data?.booking);
      showToast("OTP verified successfully.", "success");
      setVisitOtpInput("");
    } catch (error) {
      showToast(error.response?.data?.error || "OTP verify failed", "error");
    } finally {
      setModalActionLoading(false);
    }
  };

  return (
    <>
    {toast.message && (
  <div className={`toast ${toast.type}`}>
    {toast.message}
  </div>
)}
    <div className="orders-page">
      <h1 className="orders-title">Repair Orders</h1>

      <div className="orders-card">
        {loading ? <p>Loading orders...</p> : null}

        <div className="orders-toolbar">
          <div className="orders-search-wrapper">
            <Search size={18} />
            <input
              type="text"
              className="orders-search-input"
              placeholder="Search booking ID, customer, phone, brand, model, status..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>
        </div>

        <div className="orders-table-scroller">
          <table className="orders-table">
            <thead>
              <tr>
                <th>Booking ID</th>
                <th>Customer</th>
                <th>Email</th>
                <th>Brand</th>
                {/* <th>Model</th> */}
                <th>Issue</th>
                <th>Service Mode</th>
                <th>Phone</th>
                <th>Technician</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedOrders.length > 0 ? (
                paginatedOrders.map((order) => (
                  <tr key={order._id}>
                    <td data-label="Booking ID">{formatBookingId(order)}</td>
                    <td data-label="Customer">{order.name || "-"}</td>
                    <td data-label="Email">{order.email || "-"}</td>
                    <td data-label="Brand">{order.brand}</td>
                    {/* <td data-label="Model">{order.model}</td> */}
                    <td data-label="Issue">{order.service}</td>
                    <td data-label="Service Mode">{order.pickupOption || "Pickup & Drop"}</td>
                    <td data-label="Phone">{order.phone}</td>
                    <td data-label="Technician">
                      {(() => {
                        const assignedTech = order?.technicianId
                          ? technicianById.get(String(order.technicianId))
                          : null;
                        const technicianName =
                          order.technicianName ||
                          order.technician ||
                          assignedTech?.name ||
                          "Unassigned";
                        const technicianPhoto = order.technicianPhoto || assignedTech?.photoUrl || "";
                        const technicianEmployeeId =
                          order.technicianEmployeeId || assignedTech?.employeeId || "";
                        const technicianVerified = Boolean(
                          order.technicianVerified ?? assignedTech?.isVerified ?? false
                        );

                        return (
                          <div className="tech-cell">
                            <span className={`tech-avatar-sm ${technicianPhoto ? "" : "placeholder"}`}>
                              {technicianPhoto ? (
                                <img src={technicianPhoto} alt={technicianName} />
                              ) : (
                                <ImageIcon size={14} />
                              )}
                            </span>

                            <div className="tech-cell-meta">
                              <span className="tech-cell-name">{technicianName}</span>
                              <span className="tech-cell-badges">
                                {technicianVerified ? (
                                  <span className="tech-pill verified">
                                    <ShieldCheck size={12} />
                                    Verified
                                  </span>
                                ) : null}
                                {technicianEmployeeId ? (
                                  <span className="tech-pill id">
                                    <IdCard size={12} />
                                    {technicianEmployeeId}
                                  </span>
                                ) : null}
                              </span>
                            </div>
                          </div>
                        );
                      })()}
                    </td>
                    <td data-label="Status">
                      <span
                        className={`order-status ${(order.status || "Pending")
                          .toLowerCase()
                          .replace(/\s+/g, "-")}`}
                      >
                        {order.status || "Pending"}
                      </span>
                    </td>
                    <td data-label="Action">
                      <div className="order-actions">
                        <button className="order-btn view" onClick={() => setSelectedOrder(order)}>
                          <Eye size={18} />
                        </button>

                        <button className="order-btn edit" onClick={() => openEdit(order)}>
                          <Edit size={18} />
                        </button>

                        <button className="order-btn delete" onClick={() => deleteOrder(order._id)}>
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="orders-empty-row">
                    No orders found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="orders-pagination">
          <p className="orders-pagination-meta">
            Showing {visibleStart}-{visibleEnd} of {filteredOrders.length}
          </p>

          <div className="orders-pagination-right">
            <div className="orders-page-size">
              <label htmlFor="orders-page-size-select">Rows</label>
              <select
                id="orders-page-size-select"
                value={rowsPerPage}
                onChange={(event) => setRowsPerPage(Number(event.target.value))}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>

            <div className="orders-pagination-controls">
              <button
                className="orders-page-btn"
                onClick={() => setCurrentPage((previous) => Math.max(1, previous - 1))}
                disabled={safePage <= 1}
              >
                <ChevronLeft size={16} />
                Prev
              </button>

              <span className="orders-page-indicator">
                Page {safePage} / {totalPages}
              </span>

              <button
                className="orders-page-btn"
                onClick={() => setCurrentPage((previous) => Math.min(totalPages, previous + 1))}
                disabled={safePage >= totalPages}
              >
                Next
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {selectedOrder ? (
        <div className="order-modal-overlay">
          <div className="order-modal">
            <div className="order-modal-header">
              <h2>Order Details</h2>
              <X className="order-close" onClick={() => setSelectedOrder(null)} />
            </div>

            <div className="order-info">
              <strong>Booking ID:</strong> {formatBookingId(selectedOrder)}
            </div>
            <div className="order-info">
              <strong>Name:</strong> {selectedOrder.name}
            </div>
            <div className="order-info">
              <strong>Email:</strong> {selectedOrder.email || "-"}
            </div>
            <div className="order-info">
              <strong>Brand:</strong> {selectedOrder.brand}
            </div>
            <div className="order-info">
              <strong>Model:</strong> {selectedOrder.model}
            </div>
            <div className="order-info">
              <strong>Issue:</strong> {selectedOrder.service}
            </div>
            <div className="order-info">
              <strong>Service Mode:</strong> {selectedOrder.pickupOption || "Pickup & Drop"}
            </div>
            <div className="order-info">
              <strong>Phone:</strong> {selectedOrder.phone}
            </div>
            <div className="order-info">
              <strong>Technician:</strong> {selectedOrder.technicianName || selectedOrder.technician || "Unassigned"}
            </div>
            <div className="order-info">
              <strong>Technician Phone:</strong> {selectedOrder.technicianPhone || "-"}
            </div>
            <div className="order-info">
              <strong>Technician ID:</strong> {selectedOrder.technicianEmployeeId || "-"}
            </div>
            <div className="order-info">
              <strong>Verified Badge:</strong> {selectedOrder.technicianVerified ? "Yes" : "No"}
            </div>
            <div className="order-info">
              <strong>Technician Photo:</strong>{" "}
              {selectedOrder.technicianPhoto ? (
                <a href={selectedOrder.technicianPhoto} target="_blank" rel="noreferrer">
                  Open Photo
                </a>
              ) : (
                "-"
              )}
            </div>
            <div className="order-info">
              <strong>Status:</strong> {selectedOrder.status || "Pending"}
            </div>
            <div className="order-info">
              <strong>Address:</strong> {selectedOrder.address}
            </div>
            <div className="order-info">
              <strong>Pickup Location:</strong> {selectedOrder.pickupAddress || "-"}
            </div>
            <div className="order-info">
              <strong>Pickup Phone:</strong> {selectedOrder.pickupPhone || "-"}
            </div>
            <div className="order-info">
              <strong>Pickup Map:</strong>{" "}
              {getPickupMapUrl(selectedOrder) ? (
                <a href={getPickupMapUrl(selectedOrder)} target="_blank" rel="noreferrer">
                  Open Pickup Map
                </a>
              ) : (
                "-"
              )}
            </div>
            <div className="order-info">
              <strong>Live Location:</strong>{" "}
              {selectedOrder.liveLocation?.lat != null && selectedOrder.liveLocation?.lng != null
                ? `${selectedOrder.liveLocation.lat}, ${selectedOrder.liveLocation.lng}`
                : "-"}
            </div>
            <div className="order-info">
              <strong>Map:</strong>{" "}
              {getOrderMapUrl(selectedOrder) ? (
                <a href={getOrderMapUrl(selectedOrder)} target="_blank" rel="noreferrer">
                  Open Map
                </a>
              ) : (
                "-"
              )}
            </div>
            <div className="order-info">
              <strong>Admin Note:</strong> {selectedOrder.adminNote || "-"}
            </div>

            <div className="order-actions-grid">
              <button
                className="order-modal-btn"
                onClick={sendTechnicianUpdateEmail}
                disabled={modalActionLoading}
              >
                Send Gmail Update
              </button>

              <button
                className="order-modal-btn"
                onClick={sendVisitOtpToCustomer}
                disabled={modalActionLoading}
              >
                Send OTP
              </button>

              <a
                className="order-modal-btn link"
                href={getOrderMapUrl(selectedOrder) || getPickupMapUrl(selectedOrder) || "#"}
                target="_blank"
                rel="noreferrer"
                onClick={(event) => {
                  if (!(getOrderMapUrl(selectedOrder) || getPickupMapUrl(selectedOrder))) {
                    event.preventDefault();
                    showToast("No tracking location available", "error");
                  }
                }}
              >
                Open Track Page
              </a>
            </div>

            <div className="order-otp-verify">
              <input
                className="order-input"
                value={visitOtpInput}
                onChange={(event) => setVisitOtpInput(event.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="Enter customer OTP"
              />
              <button
                className="order-modal-btn verify"
                onClick={verifyVisitOtp}
                disabled={modalActionLoading}
                 style={{ color: "#FF6B00",backgroundColor: "#fff1e8" }}
              >
                Verify OTP
              </button>
            </div>

            <button className="order-save-btn" onClick={() => setSelectedOrder(null)} style={{ color: "#FF6B00",backgroundColor: "#fff1e8" }}>
              Close
            </button>
          </div>
        </div>
      ) : null}

      {editingOrder ? (
        <div className="order-modal-overlay">
          <div className="order-modal">
            <div className="order-modal-header">
              <h2>Edit Order</h2>
              <X className="order-close" onClick={() => setEditingOrder(null)} />
            </div>

            <input
              className="order-input"
              value={editingOrder.name || ""}
              onChange={(event) => setEditingOrder({ ...editingOrder, name: event.target.value })}
              placeholder="Customer Name"
            />

            <input
              className="order-input"
              value={editingOrder.phone || ""}
              onChange={(event) => setEditingOrder({ ...editingOrder, phone: event.target.value })}
              placeholder="Phone Number"
            />

            <input
              className="order-input"
              value={editingOrder.email || ""}
              onChange={(event) => setEditingOrder({ ...editingOrder, email: event.target.value })}
              placeholder="Email"
            />

            <textarea
              className="order-textarea"
              value={editingOrder.pickupAddress || ""}
              onChange={(event) => setEditingOrder({ ...editingOrder, pickupAddress: event.target.value })}
              placeholder="Full Pickup Location"
            />

            <input
              className="order-input"
              value={editingOrder.pickupPhone || ""}
              onChange={(event) => setEditingOrder({ ...editingOrder, pickupPhone: event.target.value })}
              placeholder="Pickup Contact Phone"
            />

           
            <input
              className="order-input"
              value={editingOrder.technicianPhone || ""}
              onChange={(event) => setEditingOrder({ ...editingOrder, technicianPhone: event.target.value })}
              placeholder="Technician Phone"
            />

       <select
  className="order-select"
  value={editingOrder.technicianId || ""}
  onChange={(e) => {

    const techId = e.target.value;
    const tech = technicians.find(t => t._id === techId);

    setEditingOrder({
      ...editingOrder,
      technicianId: tech?._id || "",
      technicianName: tech?.name || "",
      technicianPhone: tech?.phone || "",
      technicianEmployeeId: tech?.employeeId || "",
      technicianPhoto: tech?.photoUrl || "",
      technicianVerified: Boolean(tech?.isVerified),
    });

  }}
>

<option value="">Select Technician (Optional)</option>

{technicians.map((tech) => (
  <option key={tech._id} value={tech._id}>
    {tech.name} ({tech.phone})
  </option>
))}

</select>

            <input
              className="order-input"
              value={editingOrder.technicianEmployeeId || ""}
              onChange={(event) =>
                setEditingOrder({ ...editingOrder, technicianEmployeeId: event.target.value })
              }
              placeholder="Technician ID"
            />

            <input
              className="order-input"
              value={editingOrder.technicianPhoto || ""}
              onChange={(event) =>
                setEditingOrder({ ...editingOrder, technicianPhoto: event.target.value })
              }
              placeholder="Technician Photo URL"
            />

            <label className="order-checkbox">
              <input
                type="checkbox"
                checked={Boolean(editingOrder.technicianVerified)}
                onChange={(event) =>
                  setEditingOrder({ ...editingOrder, technicianVerified: event.target.checked })
                }
              />
              Verified badge
            </label>

            <input
              className="order-input"
              type="number"
              step="any"
              value={editingOrder.liveLat ?? ""}
              onChange={(event) => setEditingOrder({ ...editingOrder, liveLat: event.target.value })}
              placeholder="Live Latitude"
            />

            <input
              className="order-input"
              type="number"
              step="any"
              value={editingOrder.liveLng ?? ""}
              onChange={(event) => setEditingOrder({ ...editingOrder, liveLng: event.target.value })}
              placeholder="Live Longitude"
            />

            <select
              className="order-select"
              value={editingOrder.status || "Pending"}
              onChange={(event) => setEditingOrder({ ...editingOrder, status: event.target.value })}
            >
              {STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>

            <select
              className="order-select"
              value={editingOrder.pickupOption || "Pickup & Drop"}
              onChange={(event) =>
                setEditingOrder({ ...editingOrder, pickupOption: event.target.value })
              }
            >
              {PICKUP_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>

            <textarea
              className="order-textarea"
              value={editingOrder.adminNote || ""}
              onChange={(event) => setEditingOrder({ ...editingOrder, adminNote: event.target.value })}
              placeholder="Admin note"
            />
<button 
  className="order-save-btn" 
  onClick={saveUpdate} 
  style={{ color: "#FF6B00",backgroundColor: "#fff1e8" }}
>
  Save
</button>
          </div>
        </div>
      ) : null}
    </div>
    </>
  );
};

export default Orders;
